// ══════════════════════════════════════════════════════════════
// ADAPTATEUR BSI — CotaSafe
// Le calcul est désormais délégué au moteur unifié (src/lib/ngap/engine.ts).
// Ce fichier ne conserve que : la base de connaissances d'explications
// (REGLES_BSI) et la sélection des règles applicables à un résultat.
// ══════════════════════════════════════════════════════════════

import type {
  ActeSelectionne,
  ActeAnalyse,
  ForfaitBSI,
  BSIResult,
  ContradictionBSI,
  RegleAppliquee,
  MajorationBSI,
  MajorationFlags,
} from './types';
import { executerCotation } from '@/lib/ngap/engine';
import { detecterActe, normCode } from '@/lib/ngap/nomenclature';
import type { ActeDetecte } from '@/lib/ngap/types';
import { formatAMI } from '@/lib/tarifs';

// ═══ BASE DE RÈGLES (connaissances d'explication) ═══
export const REGLES_BSI: RegleAppliquee[] = [
  {
    article: 'Art. A12 Titre XVI',
    titre: 'Conversion AMX sous BSI',
    texte: 'Sous BSI, les actes techniques non dérogatoires sont cotés en AMX à 50% du coefficient. Les actes dérogatoires (pansements lourds, perfusions, ponction veineuse, Art. 5ter) sont cotés en AMX à taux plein.',
    source: 'NGAP — Titre XVI — Article A12',
    dateEntreeVigueur: '2024-01-01',
  },
  {
    article: 'Art. 11B NGAP',
    titre: 'Règle de cumul 100/50/0',
    texte: 'Lorsque plusieurs actes sont réalisés au cours du même passage, le 1er acte (coefficient le plus élevé) est facturé à 100%, le 2e à 50%, les suivants sont gratuits. IMMUNITÉS : Article 7 (postopératoire), Article 5bis (diabète insulino-traité) et séances de perfusion (AMI 9/10 + 14/15 + AMI 6).',
    source: 'NGAP — Article 11B',
    dateEntreeVigueur: '2005-01-01',
  },
  {
    article: 'Art. 11B NGAP — Dérogations de cumul',
    titre: 'Actes jamais décotés et cumuls à taux plein',
    texte: 'Le prélèvement par ponction veineuse directe (AMI 1,5) n\'est jamais décoté à 50% ; l\'analgésie topique préalable (AMI 1,1) se cumule à taux plein avec un pansement d\'ulcère ou d\'escarre.',
    source: 'NGAP — Article 11B (dérogations au non-cumul)',
    dateEntreeVigueur: '2005-01-01',
  },
  {
    article: 'Avenant 11 — Évolution 2028',
    titre: 'Article 5bis : acte unique à partir du 1er janvier 2028',
    texte: 'À compter du 1er janvier 2028, les actes de l\'article 5bis seront considérés comme un seul et unique acte pour les règles de cumul.',
    source: 'Avenant n°11 — JO du 5 mai 2026 — Article 5bis',
    dateEntreeVigueur: '2028-01-01',
  },
  {
    article: 'Art. 5bis NGAP',
    titre: 'Actes diabète insulino-traité — cumul à taux plein sans 11B',
    texte: 'L\'acte de surveillance et observation d\'un patient diabétique insulino-traité (AMI 1) se cumule à TAUX PLEIN avec l\'injection d\'insuline, la séance hebdomadaire de surveillance clinique (> 75 ans), le pansement lourd complexe, et l\'analgésie topique.',
    source: 'NGAP — Titre XVI — Article 5bis',
    dateEntreeVigueur: '2016-01-01',
  },
  {
    article: 'Art. 5bis — Bon à savoir',
    titre: 'Conditions d\'application',
    texte: 'L\'article 5 bis ne s\'applique QUE pour les patients insulino-dépendants, diagnostiqués par le médecin traitant. Les soins sans lien avec le diabète suivent la règle 100/50/0.',
    source: 'NGAP — Titre XVI — Article 5bis (conditions d\'application)',
    dateEntreeVigueur: '2016-01-01',
  },
  {
    article: 'Surveillance glycémique — Bon à savoir',
    titre: 'Contenu de l\'acte de surveillance glycémique',
    texte: 'La surveillance glycémique regroupe le contrôle de la glycémie capillaire (dextro), la traçabilité des résultats, l\'injection d\'insuline et l\'éducation à l\'autosurveillance.',
    source: 'CotaSafe — Aide à la cotation (surveillance glycémique)',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'Règle CotaSafe — Glycémie absorbée',
    titre: 'Glycémie capillaire = élément de l\'acte de surveillance',
    texte: 'Lorsque la glycémie capillaire est réalisée dans le cadre de la surveillance d\'un patient diabétique insulino-traité, elle est absorbée par l\'acte de surveillance glycémique (Art. 5bis, AMI 1).',
    source: 'CotaSafe — Règle d\'absorption automatique',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'Règle CotaSafe — Cohérence insuline/glycémie',
    titre: 'L\'insuline exige une glycémie ; la 2e insuline exige une 1re insuline',
    texte: 'La glycémie capillaire est le prérequis de sécurité de l\'injection d\'insuline ; la « 2e insuline » ne peut être cotée qu\'en présence d\'une 1re injection d\'insuline le même jour.',
    source: 'CotaSafe — Règle de cohérence insuline/glycémie',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'Avenant 6 — BSI + Diabète',
    titre: 'Surveillance glycémique et insuline cumulables avec le forfait BSI (AMX à 50%)',
    texte: 'Les surveillances glycémiques et les injections d\'insuline sont cumulables avec les forfaits BSI, en AMX à 50% chacune.',
    source: 'Avenant n°6 — Convention nationale IDEL',
    dateEntreeVigueur: '2024-01-01',
  },
  {
    article: 'Art. 5ter NGAP',
    titre: 'Surveillance post-hospitalisation',
    texte: 'Surveillance clinique renforcée post-hospitalisation (AMI 5,8) : limitée à 15 séances sur prescription médicale. Acte dérogatoire — facturable à taux plein même sous BSI.',
    source: 'NGAP — Titre XVI — Article 5ter',
    dateEntreeVigueur: '2018-01-01',
  },
  {
    article: 'PRADO — Programme de retour à domicile',
    titre: 'Cotation du suivi PRADO par une IDEL (AMI 5,8)',
    texte: 'Le suivi PRADO se cote AMI 5,8 (' + formatAMI(5.8) + '). Cumul à taux plein (Art. 11B dérogatoire) avec un acte technique. Non cumulable avec une autre surveillance clinique ni avec les majorations nuit/dimanche. Fiche de suivi à transmettre sous 48h.',
    source: 'NGAP — PRADO (Programme de retour à domicile)',
    dateEntreeVigueur: '2018-01-01',
  },
  {
    article: 'Art. 6 NGAP',
    titre: 'Dialyse péritonéale à domicile',
    texte: 'Séance de dialyse péritonéale (AMI 4) : maximum 4 séances par jour.',
    source: 'NGAP — Article 6',
    dateEntreeVigueur: '2012-01-01',
  },
  {
    article: 'Art. 7 NGAP',
    titre: 'Soins postopératoires (RAAC)',
    texte: 'Les actes postopératoires (surveillance AMI 3,9, drain Redon AMI 2,8, cathéter périnerveux AMI 4,2, retrait sonde urinaire AMI 2) se cumulent à taux plein. Le drain de Redon ne se cumule PAS avec le cathéter périnerveux.',
    source: 'NGAP — Article 7',
    dateEntreeVigueur: '2019-01-01',
  },
  {
    article: 'Art. 7 NGAP — Drain de Redon',
    titre: 'Surveillance/retrait de drain de Redon (AMI 2,8)',
    texte: 'Limité à 2 séances maximum par prescription/séjour à domicile. Cumul à taux plein avec la surveillance postopératoire (AMI 3,9) et le retrait de sonde urinaire (AMI 2). Non-cumul avec le cathéter périnerveux (AMI 4,2).',
    source: 'NGAP — Article 7 — Dispositions générales',
    dateEntreeVigueur: '2019-01-01',
  },
  {
    article: 'Art. 10 NGAP',
    titre: 'Surveillance de traitement',
    texte: 'Administration de traitement : incluse dans le forfait BSI. Hors BSI : AMI 1,2, non cumulable avec la surveillance TA dans la même séance. Limités à 15 jours.',
    source: 'NGAP — Article 10',
    dateEntreeVigueur: '2005-01-01',
  },
  {
    article: 'Prise de constantes',
    titre: 'Tension, pouls, température — pas de cotation isolée',
    texte: 'La prise de constantes n\'a pas de cotation isolée dans la NGAP : intégrée à une surveillance clinique réglementée (AMI 5,8), à un forfait de dépendance, ou associée à un soin technique.',
    source: 'NGAP — Prise de constantes',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'IFD / IFI — Déplacement',
    titre: 'Indemnité de déplacement (IFD hors BSI, IFI sous BSI)',
    texte: '2,75 € par visite (matin, midi, soir). Les actes de télésoin ne donnent PAS droit à l\'IFD/IFI.',
    source: 'NGAP — Art. 13 — Indemnités de déplacement',
    dateEntreeVigueur: '2024-01-01',
  },
  {
    article: 'Forfait BSI 2024',
    titre: 'Contenu du forfait dépendance',
    texte: 'Les forfaits BSA/BSB/BSC incluent : toilette, change, nursing, hydratation, alimentation, prévention des escarres, mobilisation, administration de traitement, pansements simples. Seuls les pansements lourds et actes dérogatoires restent facturables en sus.',
    source: 'BSI 2024 — Contenu des forfaits dépendance',
    dateEntreeVigueur: '2024-01-01',
  },
  {
    article: 'Art. 11B NGAP — AMI 11',
    titre: 'Bilan initial pansements lourds et complexes (AMI 11)',
    texte: 'Un seul AMI 11 par patient par période de 12 mois. Non cumulable avec la MCI. L\'AMI 11 englobe déjà le pansement complexe du jour.',
    source: 'NGAP — Article 11B — Bilan initial pansements lourds et complexes',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'CIR-9/2025 — AMI 5',
    titre: 'AMI 5 redéfini : arrêt définitif de perfusion > 24h ou retrait Midline/Picc Line',
    texte: 'L\'AMI 5 est limité à l\'arrêt définitif d\'une perfusion de plus de 24h et au retrait définitif d\'un Midline/Picc Line. L\'arrêt < 24h est coté AMI 4,1.',
    source: 'Circulaire CIR-9/2025',
    dateEntreeVigueur: '2025-01-01',
  },
  {
    article: 'CIR-9/2025 — AMI 4',
    titre: 'AMI 4 : organisation de la surveillance d\'une perfusion longue',
    texte: 'L\'AMI 4 est facturé PAR JOUR, HORS jour de pose (AMI 14/15) et HORS jour de retrait (AMI 5). Pas de frais de déplacement ni de majorations nuit/dimanche.',
    source: 'Circulaire CIR-9/2025',
    dateEntreeVigueur: '2025-01-01',
  },
  {
    article: 'CIR-9/2025 — AMI 4,1',
    titre: 'AMI 4,1 : actes associés aux perfusions longues',
    texte: 'Changement de flacon, surveillance du débit, branchement en Y, déplacement. Interdit lors du passage facturé AMI 14/15. En branchement Y : 1er à taux plein, 2e à demi-tarif.',
    source: 'Circulaire CIR-9/2025',
    dateEntreeVigueur: '2025-01-01',
  },
  {
    article: 'CIR-9/2025 — AMI 9/10',
    titre: 'Perfusions < 1h sous surveillance permanente : AMI 9 / AMI 10 inchangés',
    texte: 'AMI 9 pour la perfusion courte (≤ 1h), AMI 10 pour le patient cancéreux/immunodéprimé. Restent dérogatoires (taux plein même sous BSI).',
    source: 'Circulaire CIR-9/2025',
    dateEntreeVigueur: '2025-01-01',
  },
  {
    article: 'NGAP — Perfusions',
    titre: 'Cumul des séances de perfusion et AMI 4,1 associé',
    texte: 'Les séances de perfusion courte se cumulent à taux plein entre elles ET avec une perfusion longue. Une seule perfusion longue par jour ; une seule séance courte par jour.',
    source: 'NGAP — Article 11B (exceptions perfusion)',
    dateEntreeVigueur: '2026-01-01',
  },
  {
    article: 'CIR-9/2025 — AMI 6',
    titre: 'AMI 6 : heure supplémentaire de surveillance continue',
    texte: 'Chaque heure supplémentaire au-delà de la 1ère heure d\'une perfusion courte AMI 9/10, sur prescription. Durée totale limitée à 5 heures.',
    source: 'Circulaire CIR-9/2025 + NGAP — Perfusions',
    dateEntreeVigueur: '2025-01-01',
  },
  {
    article: 'Avenant n°9 — Télésoin',
    titre: 'Actes de télésoin (TMI, TLS, TLL, TLD) — toujours à taux plein, non cumulables',
    texte: 'Jamais décotés, jamais convertis en AMX. Non cumulables avec IFD/IFI, MCI, forfaits BSI, actes techniques, ni entre eux (exception : suivi de pansement TMI 1,6 cumulable avec un pansement).',
    source: 'Avenant n°9 — Convention nationale IDEL — Télésoin',
    dateEntreeVigueur: '2019-01-01',
  },
  {
    article: 'MCI — Majoration de Coordination Infirmière',
    titre: '5 € par passage avec soin complexe',
    texte: '5,00 € par passage lorsqu\'un soin complexe est effectué (pansement lourd, perfusion, soins palliatifs). Exige un échange documenté avec le médecin traitant. Non cumulable avec l\'AMI 11 ni les télésoins.',
    source: 'Avenant 6 — Convention nationale IDEL — Art. 23.2 NGAP',
    dateEntreeVigueur: '2025-01-01',
  },
];

// ═══ HELPERS (utilisés par getReglesApplicables) ═══
function estActeGlycemie(label: string): boolean {
  const l = label.toLowerCase();
  return l.includes('glycém') || l.includes('glycem') || l.includes('dextro') ||
    l.includes('surveillance glyc') || l.includes('contrôle glyc') || l.includes('controle glyc');
}

function estActeInsuline(label: string): boolean {
  return label.toLowerCase().includes('insuline');
}

function estDeuxiemeInsuline(label: string): boolean {
  const l = label.toLowerCase();
  return l.includes('deuxième') || l.includes('deuxieme') ||
    l.includes('2e insuline') || l.includes('2ème insuline') || l.includes('2eme insuline') ||
    l.includes('2e ligne') || l.includes('2eme ligne') || l.includes('2e injection') ||
    l.includes('seconde insuline') || l.includes('insuline différente') || l.includes('insuline differente');
}

function estInsulinePremiere(label: string): boolean {
  return estActeInsuline(label) && !estDeuxiemeInsuline(label);
}

function estConstantes(keyword: string, label?: string): boolean {
  const k = keyword.toLowerCase();
  const l = (label || '').toLowerCase();
  return k.includes('tension') || k.includes('pouls') || k.includes('température') ||
    k.includes('temperature') || k.includes('constantes') || k.includes('pression artérielle') ||
    k.includes('pression arterielle') || l.includes('constantes');
}

// ═══ ANALYSE BSI — adaptateur fin vers le moteur unifié ═══
export function analyserBSI(actes: ActeSelectionne[], flags?: MajorationFlags): BSIResult {
  const periodMap: Record<'matin' | 'midi' | 'soir', string> = {
    matin: 'Matin (08h00)',
    midi: 'Midi (12h00)',
    soir: 'Soir (19h00)',
  };

  const actesDetectes: ActeDetecte[] = [];
  const seenDaily = new Set<string>();

  for (const sel of actes) {
    const def = detecterActe(sel.acte.keyword);
    if (!def) continue;
    if (def.isDaily) {
      if (seenDaily.has(def.code)) continue;
      seenDaily.add(def.code);
      actesDetectes.push({ id: sel.acte.id, definition: def, periode: 'journee', passage: 'Journée', sourceKeyword: sel.acte.keyword });
    } else {
      actesDetectes.push({ id: sel.acte.id, definition: def, periode: sel.periode, passage: periodMap[sel.periode], sourceKeyword: sel.acte.keyword });
    }
  }

  const resultat = executerCotation(actesDetectes, flags);

  const forfait: ForfaitBSI | null = resultat.contexte.forfait
    ? {
        code: resultat.contexte.forfait.code,
        label: resultat.contexte.forfait.label,
        montantJournalier: resultat.contexte.forfait.montantJournalier,
        niveauGIR: resultat.contexte.forfait.niveauGIR,
      }
    : null;

  const actesAnalyse: ActeAnalyse[] = resultat.actes
    .filter((a) => a.categorie !== 'Forfait dépendance' && a.categorie !== 'Démarche de soins')
    .map((a) => ({
      acte: {
        acte: { id: a.id, label: a.label, keyword: a.sourceKeyword || a.label, categorie: a.categorie },
        periode: (a.periode === 'journee' ? 'matin' : a.periode) as 'matin' | 'midi' | 'soir',
      },
      codeNGAP: a.code,
      coefficient: a.coefficient,
      baseAmount: a.baseAmount,
      categorieNGAP: a.categorie,
      article: a.article,
      derogatoire: a.derogatoire,
      article5bis: a.article5bis,
      article7: a.article7,
      article5ter: a.article5ter,
      article6: a.article6,
      noMCI: a.noMCI,
      jamaisDecote: a.jamaisDecote,
      analgesieTopique: a.analgesieTopique,
      cumulableAvecAnalgesie: a.cumulableAvecAnalgesie,
      inclusDansForfait: a.inclusDansForfait,
      facturableEnComplement: !a.inclusDansForfait,
      exceptionAppliquee: a.exceptionAppliquee,
      regleSpecifique: null,
      regleCumul: a.regleCumul,
      tauxApplique: a.tauxFacturation,
      montantFinal: a.montant,
      raison: a.justification,
    }));

  const majorations: MajorationBSI[] = resultat.majorations
    .filter((m) => m.code !== 'IFD')
    .map((m) => ({ code: m.code, nom: m.nom, applicable: m.applicable, montant: m.montant, raison: m.raison, article: m.article }));

  const contradictions: ContradictionBSI[] = resultat.contradictions.map((c) => ({
    type: c.type,
    titre: c.titre,
    description: c.description,
    suggestion: c.suggestion,
    severite: c.severite,
  }));

  const explications = [...resultat.explications];
  if (forfait && resultat.contexte.ifi.nbVisites > 0) {
    explications.push(
      `Indemnité de déplacement : ${resultat.contexte.ifi.nbVisites} visite(s) × IFI 2,75 € = ${resultat.contexte.ifi.totalIFI.toFixed(2).replace('.', ',')} €.`
    );
  }

  return {
    forfait,
    actes: actesAnalyse,
    totalJournalier: resultat.total.totalGeneral,
    ifi: resultat.contexte.ifi,
    majorations,
    explications,
    avertissements: resultat.avertissements,
    contradictions,
  };
}

// ═══ RÈGLES APPLICABLES (sélection pour l'affichage) ═══
export function getReglesApplicables(result: BSIResult): RegleAppliquee[] {
  const regles: RegleAppliquee[] = [];
  const allRules = new Map(REGLES_BSI.map((r) => [r.article, r]));

  const hasMultiActes = result.actes.filter((a) => !a.inclusDansForfait && a.coefficient > 0).length >= 2;
  if (hasMultiActes) regles.push(allRules.get('Art. 11B NGAP')!);

  const hasAMX = result.actes.some((a) => a.codeNGAP.includes('AMX'));
  if (hasAMX && result.forfait) regles.push(allRules.get('Art. A12 Titre XVI')!);

  if (result.forfait) {
    regles.push(allRules.get('Forfait BSI 2024')!);
    regles.push(allRules.get('IFD / IFI — Déplacement')!);
  }

  const has5bis = result.actes.some((a) => a.article5bis);
  if (has5bis) {
    regles.push(allRules.get('Art. 5bis NGAP')!);
    const rBon = allRules.get('Art. 5bis — Bon à savoir');
    if (rBon) regles.push(rBon);
    const rSurvGly = allRules.get('Surveillance glycémique — Bon à savoir');
    if (rSurvGly) regles.push(rSurvGly);
  }

  const hasFusionGlycemie = result.explications.some(
    (e) => e.includes('Fusion glycémie') || e.includes('absorbé')
  );
  if (hasFusionGlycemie) {
    const rAbsorb = allRules.get('Règle CotaSafe — Glycémie absorbée');
    if (rAbsorb) regles.push(rAbsorb);
  }

  if (result.actes.some((a) => a.article5ter)) regles.push(allRules.get('Art. 5ter NGAP')!);
  if (result.actes.some((a) => a.article7)) regles.push(allRules.get('Art. 7 NGAP')!);

  const hasDrainRedon = result.actes.some((a) => {
    const kw = a.acte.acte.keyword.toLowerCase();
    return (kw.includes('drain') && kw.includes('redon')) || kw.includes('drain de redon');
  });
  if (hasDrainRedon) {
    const rRedon = allRules.get('Art. 7 NGAP — Drain de Redon');
    if (rRedon) regles.push(rRedon);
  }

  if (result.actes.some((a) => normCode(a.codeNGAP) === 'AMI 5')) {
    const r5 = allRules.get('CIR-9/2025 — AMI 5');
    if (r5) regles.push(r5);
  }
  if (result.actes.some((a) => normCode(a.codeNGAP) === 'AMI 4,1')) {
    const r41 = allRules.get('CIR-9/2025 — AMI 4,1');
    if (r41) regles.push(r41);
  }
  if (result.actes.some((a) => normCode(a.codeNGAP) === 'AMI 4' && a.categorieNGAP === 'Perfusions')) {
    const r4 = allRules.get('CIR-9/2025 — AMI 4');
    if (r4) regles.push(r4);
  }
  if (result.actes.some((a) => normCode(a.codeNGAP) === 'AMI 9' || normCode(a.codeNGAP) === 'AMI 10')) {
    const r910 = allRules.get('CIR-9/2025 — AMI 9/10');
    if (r910) regles.push(r910);
  }
  if (result.actes.some((a) => normCode(a.codeNGAP) === 'AMI 6')) {
    const r6 = allRules.get('CIR-9/2025 — AMI 6');
    if (r6) regles.push(r6);
  }

  if (result.actes.some((a) => a.categorieNGAP === 'Télésoin')) {
    const rTel = allRules.get('Avenant n°9 — Télésoin');
    if (rTel) regles.push(rTel);
  }

  const hasPerfusionSeance = result.actes.some((a) =>
    normCode(a.codeNGAP) === 'AMI 9' || normCode(a.codeNGAP) === 'AMI 10' ||
    normCode(a.codeNGAP) === 'AMI 14' || normCode(a.codeNGAP) === 'AMI 15'
  );
  if (hasPerfusionSeance) {
    const rPerf = allRules.get('NGAP — Perfusions');
    if (rPerf) regles.push(rPerf);
  }

  const hasJamaisDecote = result.actes.some((a) => a.jamaisDecote);
  const hasAnalgesie = result.actes.some((a) => a.analgesieTopique);
  if (hasJamaisDecote || hasAnalgesie) {
    const rDerog = allRules.get('Art. 11B NGAP — Dérogations de cumul');
    if (rDerog) regles.push(rDerog);
  }

  if (result.actes.some((a) => a.article.includes('Art. 10'))) regles.push(allRules.get('Art. 10 NGAP')!);

  const hasConstantes = result.actes.some(
    (a) => !a.inclusDansForfait && estConstantes(a.acte.acte.keyword, a.acte.acte.label)
  );
  if (hasConstantes) {
    const rConst = allRules.get('Prise de constantes');
    if (rConst) regles.push(rConst);
  }

  const hasMCI = result.majorations.some((m) => m.code === 'MCI' && m.applicable);
  if (hasMCI) {
    const rMCI = allRules.get('MCI — Majoration de Coordination Infirmière');
    if (rMCI) regles.push(rMCI);
  }

  const hasBilanInitial = result.actes.some((a) => !a.inclusDansForfait && normCode(a.codeNGAP) === 'AMI 11');
  if (hasBilanInitial) {
    const rAMI11 = allRules.get('Art. 11B NGAP — AMI 11');
    if (rAMI11) regles.push(rAMI11);
  }

  const hasInsulineAct = result.actes.some((a) => !a.inclusDansForfait && estActeInsuline(a.acte.acte.keyword));
  const hasGlycemieAct = result.actes.some((a) => !a.inclusDansForfait && estActeGlycemie(a.acte.acte.keyword));
  const hasDeuxiemeInsu = result.actes.some((a) => estDeuxiemeInsuline(a.acte.acte.keyword));
  const hasPremiereInsu = result.actes.some((a) => estInsulinePremiere(a.acte.acte.keyword));
  if ((hasInsulineAct && !hasGlycemieAct) || (hasDeuxiemeInsu && !hasPremiereInsu)) {
    const rCoherence = allRules.get('Règle CotaSafe — Cohérence insuline/glycémie');
    if (rCoherence) regles.push(rCoherence);
  }

  regles.push({
    article: 'Mise à jour',
    titre: 'Tarifs applicables',
    texte: 'Tarifs NGAP 2026 — Métropole. Dernière mise à jour : août 2026. Revalorisation AMI/AMX : 3,15 € jusqu\'à fin octobre 2026, puis 3,35 € (nov. 2026) et 3,45 € (nov. 2027).',
    source: 'NGAP 2026',
    dateEntreeVigueur: '2026-01-01',
  });

  return regles;
}